<template>
    <div class="w-full">
        <!-- Header -->
        <div v-if="title || itemsPerPage > 0" class="flex justify-between flex-col lg:flex-row items-center mb-4 gap-3">
            <div class="w-full lg:w-auto flex justify-start items-center gap-2">
                <h2 v-if="title" class="text-lg font-semibold text-gray-800">
                    {{ title }}<span v-if="filteredItems.length > 0"> : {{ filteredItems.length }}</span>
                </h2>
            </div>
            
            <div v-if="itemsPerPage > 0 && filteredItems.length > 0" class="flex lg:flex-row items-center justify-between gap-3 w-full lg:w-auto">
                <div class="flex justify-between items-center">
                    <div class="flex items-center gap-1 sm:gap-2 bg-white px-1.5 py-1.5 rounded-md border border-gray-300 shadow-sm">
                        <label class="text-xs font-medium text-gray-500">Ver:</label>
                        <select v-model="itemsPerPage" @change="currentPage = 1" class="text-xs py-1 pl-1 pr-6 border focus:ring-0 outline-none">
                            <option :value="10">10 registros</option>
                            <option :value="20">20 registros</option>
                            <option :value="50">50 registros</option>
                            <option :value="100">100 registros</option>
                        </select>
                    </div>
                </div>
                
                <div class="flex justify-between items-center">
                    <div class="flex items-center gap-1 sm:gap-2 bg-white pl-1.5 pr-2 sm:pr-3 py-1.5 rounded-md border border-gray-300 shadow-sm">
                        <select v-model="currentPage" @change="changePage(currentPage)" class="text-xs py-1 pl-1 pr-6 sm:hidden border focus:ring-0 outline-none">
                            <option v-for="n in totalPages" :key="n" :value="n" class="pr-3">
                                Pág. {{ n }}
                            </option>
                        </select>
                        <select v-model="currentPage" @change="changePage(currentPage)" class="text-xs py-1 pl-1 pr-8 hidden sm:block border focus:ring-0 outline-none">
                            <option v-for="n in totalPages" :key="n" :value="n" class="pr-3">
                                Página {{ n }}
                            </option>
                        </select>
                        <span class="text-xs text-gray-400">de {{ totalPages }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Simple Search Block -->
        <div v-if="simpleSearch && items && items.length" class="flex flex-col sm:flex-row items-center gap-2 mb-4">
            <select v-model="searchCol" class="w-full sm:w-auto text-sm">
                <option value="">Buscar por...</option>
                <template v-for="col in visibleHeaders" :key="'ss-' + col.key">
                    <option v-if="col.key !== 'actions'" :value="col.key">{{ col.label }}</option>
                </template>
            </select>
            
            <div class="relative w-full sm:w-[300px]">
                <input v-model="searchText" type="text" class="w-full text-sm" placeholder="Escriba para buscar..." :disabled="!searchCol" />
            </div>
            
            <button v-if="searchText" @click="searchText = ''" class="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors text-sm font-medium" title="Limpiar búsqueda">
                Limpiar
            </button>
        </div>

        <div class="overflow-x-auto overflow-hidden min-h-[450px]" :class="[spaced ? 'border-0 lg:border' : 'border', (itemsPerPage > 0 && paginatedItems && paginatedItems.length > 0) ? 'rounded-t-md' : 'rounded-md']">
            <template v-if="filteredItems && filteredItems.length">
                <table class="w-full block lg:table text-sm text-left rtl:text-right text-gray-700">
                <thead v-if="headers && headers.length" class="hidden lg:table-header-group text-sm text-gray-900 bg-gray-100 border-b sticky top-0 z-10">
                    <tr>
                        <th v-for="header in visibleHeaders" :key="header.key" :class="[header.class, 'lg:p-3']" :style="header.style" :title="header.title" class="text-center font-normal lg:font-semibold tracking-wider whitespace-nowrap">
                            <!-- Slot for header customization -->
                            <slot :name="`header-${header.key}`" :header="header">
                                {{ header.label }}
                            </slot>
                        </th>
                    </tr>
                </thead>
                <tbody class="block w-full lg:table-row-group">
                    <tr v-for="(item, index) in paginatedItems" :key="item.id || index" 
                        class="w-full max-w-full flex flex-col relative lg:table-row lg:hover:bg-gray-200/70 odd:lg:bg-gray-100/50"
                        :class="spaced ? 'mb-5 border border-gray-300 rounded-lg shadow-md lg:mb-0 lg:border-0 lg:border-b lg:border-gray-100 lg:rounded-none lg:shadow-none overflow-hidden' : 'border-b-4 border-gray-100 lg:border-b'">
                        <td v-for="header in visibleHeaders" :key="header.key" :data-label="header.label" :class="[header.cellClass, header.standout ? 'bg-gray-100 lg:bg-transparent' : '', header.key === 'actions' ? 'order-last !border-b-0' : '', header.stacked ? 'grid grid-cols-1 gap-2 text-left' : 'flex justify-between text-right', header.hideLabel ? 'before:hidden' : '', 'py-3 px-3 align-top border-b border-gray-100 lg:table-cell lg:p-3 lg:align-middle lg:border-b-0 text-sm [&:not(:first-child)]:lg:border-l [&:not(:first-child)]:lg:border-t-0 [&:not(:first-child)]:lg:border-r-0 [&:not(:first-child)]:lg:border-b-0 [&:not(:first-child)]:lg:border-gray-100 before:content-[attr(data-label)] before:font-semibold before:pr-0 lg:before:pr-3 before:text-left lg:before:hidden before:text-[0.8rem] before:whitespace-nowrap lg:before:text-sm']">
                            <!-- Slot for cell customization -->
                            <div class="text-sm flex items-center gap-2 w-full" :class="[
                                (header.stacked || header.hideLabel) 
                                    ? (header.align === 'center' ? 'justify-center text-center' : (header.align === 'right' ? 'justify-end text-right' : 'justify-start text-left'))
                                    : (header.align === 'between' ? 'justify-between' : 'justify-end text-right'),
                                header.align === 'center' ? 'lg:justify-center lg:text-center' : (header.align === 'right' ? 'lg:justify-end lg:text-right' : 'lg:justify-start lg:text-left')
                            ]">
                                <slot :name="`cell-${header.key}`" :item="item" :index="index">
                                    <span v-if="header.maxLength" :title="String(item[header.key] || '')">
                                        {{ truncateText(item[header.key], header.maxLength) }}
                                    </span>
                                    <template v-else>
                                        {{ item[header.key] }}
                                    </template>
                                </slot>
                            </div>
                        </td>
                    </tr>
                </tbody>
                </table>
            </template>
            <div v-else class="flex flex-col items-center justify-center min-h-[300px]">
                <div class="text-gray-400 text-center p-5">
                    
                    <div class="text-sm">
                        <slot name="empty">
                            <p v-if="searchText">No se encontraron resultados para su búsqueda.</p>
                            <p v-else>No hay registros para mostrar</p>
                        </slot>
                    </div>
                </div>
            </div>
        </div>

        <!-- Paginación -->
        <div v-if="itemsPerPage > 0 && paginatedItems && paginatedItems.length > 0" class="flex flex-col sm:flex-row items-center justify-between p-4 bg-white gap-3 border-t-0" :class="spaced ? 'border-0 lg:border lg:border-t-0 rounded-b-md' : 'border rounded-b-md'">
            <div class="text-xs text-gray-500">
                Mostrando <span class="font-semibold text-gray-700">{{ paginatedItems.length }}</span> de <span class="font-semibold text-gray-700">{{ filteredItems.length }}</span> registros
            </div>
            <div class="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
                <span class="text-xs text-gray-500 whitespace-nowrap">Página {{ currentPage }} de {{ totalPages }}</span>
                <div class="inline-flex shadow-sm rounded-md flex-wrap justify-center">
                    <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1" class="px-3 py-1.5 text-sm font-medium border rounded-l-lg transition-colors border-gray-300" :class="[currentPage === 1 ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50']">
                        Ant.
                    </button>
                    <template v-if="showPageNumbers">
                        <button v-for="page in pageNumbers" :key="page" @click="changePage(page)" class="inline-block px-3 py-1.5 text-sm font-medium border-t border-b border-r border-gray-300 transition-colors" :class="[currentPage === page ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50']">
                            {{ page }}
                        </button>
                    </template>
                    <button @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages" class="px-3 py-1.5 text-sm font-medium border border-l-0 rounded-r-lg transition-colors border-gray-300" :class="[currentPage === totalPages ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50']">
                        Sig.
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
    headers: {
        type: Array, // Array of { key: string, label: string, title?: string, class?: string, cellClass?: string, condition?: boolean, align?: string, standout?: boolean }
        required: true
    },
    items: {
        type: Array,
        required: true
    },
    spaced: {
        type: Boolean,
        default: false
    },
    simpleSearch: {
        type: Boolean,
        default: false
    },
    perPage: {
        type: Number,
        default: 20
    },
    title: {
        type: String,
        default: ''
    },
    showPageNumbers: {
        type: Boolean,
        default: true
    }
});

const visibleHeaders = computed(() => {
    return props.headers.filter(h => h.condition === undefined || h.condition);
});

const searchCol = ref('');
const searchText = ref('');

const filteredItems = computed(() => {
    if (!props.simpleSearch || !searchCol.value || !searchText.value) {
        return props.items;
    }
    const term = searchText.value.toLowerCase();
    return props.items.filter(item => {
        const val = item[searchCol.value];
        if (val === null || val === undefined) return false;
        return String(val).toLowerCase().includes(term);
    });
});

const currentPage = ref(1);
const itemsPerPage = ref(props.perPage);

watch(() => props.perPage, (newVal) => {
    itemsPerPage.value = newVal;
});

const paginatedItems = computed(() => {
    if (itemsPerPage.value > 0) {
        const start = (currentPage.value - 1) * itemsPerPage.value;
        return filteredItems.value.slice(start, start + itemsPerPage.value);
    }
    return filteredItems.value;
});

const totalPages = computed(() => {
    if (itemsPerPage.value > 0) {
        return Math.ceil(filteredItems.value.length / itemsPerPage.value) || 1;
    }
    return 1;
});

const changePage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
    }
};

const pageNumbers = computed(() => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages.value, start + maxVisible - 1);
    
    if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
        pages.push(i);
    }
    return pages;
});

watch(filteredItems, () => {
    currentPage.value = 1;
});

const truncateText = (text, length) => {
    if (text === null || text === undefined || !length) return text;
    const str = String(text);
    if (str.length <= length) return str;
    return str.substring(0, length) + '...';
};
</script>

<!--
=======================================================================
DOCUMENTACIÓN DEL COMPONENTE TABLERESPONSIVE
=======================================================================
Este componente es una tabla responsiva diseñada para mostrar datos
de forma óptima tanto en escritorio como en dispositivos móviles. 
En modo móvil (celulares y tablets pequeñas), las filas se convierten 
automáticamente en tarjetas apiladas para mejor lectura.

PROPS DISPONIBLES:
-----------------------------------------------------------------------
:items            [Array] Requerido. El arreglo de objetos a mostrar en la tabla.
:headers          [Array] Requerido. Configuración de las columnas. Ver más abajo.
:spaced           [Boolean] (Default: false) Separa las tarjetas móviles con margen inferior y bordes completos (ideal para layouts con espacios).
:simpleSearch     [Boolean] (Default: false) Habilita una barra superior con un buscador local y selección de columna.
:perPage          [Number] (Default: 20) Cantidad de registros por página. Activa la paginación local. Ponlo a 0 para desactivar la paginación.
:title            [String] Título opcional que aparecerá en la parte superior izquierda del encabezado.
:showPageNumbers  [Boolean] (Default: true) Muestra los botones numéricos de paginación entre los controles Ant. y Sig. Ponlo a false para ocultarlos.

CONFIGURACIÓN DE COLUMNAS (:headers):
-----------------------------------------------------------------------
Es un arreglo de objetos. Cada objeto representa una columna.

key:              [String] Requerido. Propiedad del objeto a mostrar (e.g., 'name'). Si usas 'actions', la columna se irá visualmente al final en móviles.
label:            [String] Requerido. Título que aparecerá en el encabezado de la columna.
align:            [String] 'left', 'center', 'right', 'between'. Alineación del contenido (muy útil en móviles cuando se combina con stacked o hideLabel).
class:            [String] Clases CSS adicionales para el encabezado (<th>).
cellClass:        [String] Clases CSS adicionales para la celda (<td>).
condition:        [Boolean] Si es false, la columna se oculta completamente de la tabla.
standout:         [Boolean] Si es true, otorga un fondo gris a la celda en móviles para resaltarla (común para la primera columna identificadora).
stacked:          [Boolean] Si es true, en modo móvil el título aparece arriba y el contenido ocupa el 100% del ancho en la línea de abajo.
hideLabel:        [Boolean] Si es true, oculta el título de la celda en modo móvil permitiendo centrar el contenido (ideal para botones de acción).
maxLength:        [Number] Si se define (ej. 25), trunca el texto de la celda a esa cantidad de caracteres y le añade "..."

SLOTS DISPONIBLES:
-----------------------------------------------------------------------
La tabla soporta personalización total de cualquier celda usando el formato "cell-key":
#header-{key}="{ header }"   -> Personalizar un encabezado en específico.
#cell-{key}="{ item, index }" -> Personalizar el interior de una celda.
#empty                       -> Personalizar el mensaje cuando la tabla no tiene datos.

EJEMPLO DE USO:
-----------------------------------------------------------------------
<TableResponsive 
    :items="usuarios" 
    :headers="[
        { key: 'name', label: 'Nombre', standout: true, maxLength: 25 },
        { key: 'description', label: 'Descripción', stacked: true },
        { key: 'actions', label: 'Acciones', align: 'center', hideLabel: true }
    ]"
    title="Lista de Usuarios"
    :perPage="10"
    simpleSearch
    spaced
>
    <template #cell-actions="{ item }">
        <button @click="editar(item)">Editar</button>
    </template>
</TableResponsive>
=======================================================================
-->